import { useCallback } from "react";
import { toast as sonnerToast } from "sonner";
import type { ToastOptions, ToastType } from "./types";
import { DEFAULT_DURATION } from "./types";

function showSonnerToast(options: ToastOptions): string | number {
  const {
    type = "info",
    title,
    description,
    duration = DEFAULT_DURATION,
    action,
  } = options;

  const sonnerAction = action
    ? { label: action.label, onClick: action.onClick }
    : undefined;

  const toastFn = {
    success: sonnerToast.success,
    error: sonnerToast.error,
    warning: sonnerToast.warning,
    info: sonnerToast.info,
  }[type];

  return toastFn(title, {
    description,
    duration: duration === 0 ? Infinity : duration,
    action: sonnerAction,
  });
}

export function useToast() {
  const showToast = useCallback((options: ToastOptions) => {
    return showSonnerToast(options);
  }, []);

  const showSuccess = useCallback((title: string, description?: string) => {
    return showSonnerToast({ type: "success", title, description });
  }, []);

  const showError = useCallback((title: string, description?: string) => {
    return showSonnerToast({ type: "error", title, description });
  }, []);

  const showWarning = useCallback((title: string, description?: string) => {
    return showSonnerToast({ type: "warning", title, description });
  }, []);

  const showInfo = useCallback((title: string, description?: string) => {
    return showSonnerToast({ type: "info", title, description });
  }, []);

  const dismissToast = useCallback((id: string | number) => {
    sonnerToast.dismiss(id);
  }, []);

  const dismissAll = useCallback(() => {
    sonnerToast.dismiss();
  }, []);

  const showLoading = useCallback((title: string, description?: string) => {
    return sonnerToast.loading(title, { description });
  }, []);

  return {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    dismissToast,
    dismissAll,
  };
}

// Direct export for use outside React components
export { showSonnerToast as toast };
export type { ToastType };
